import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { MoreHorizontal } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Modal, Drawer, useToast, useConfirm, CountrySelect, ErrorState } from '@/components/ui';
import { contactsApi, Contact } from '@/lib/contactsApi';
import { accountsApi } from '@/lib/accountsApi';
import { signalsApi } from '@/lib/signalsApi';
import { CommentThread } from '@/components/comments/CommentThread';
import { formatLocaleDate } from '@/lib/date';

interface ContactFormState {
  displayName: string;
  email: string;
  title: string;
  department: string;
  countryCode: string;
  phone: string;
  accountId: string | null;
}

const EMPTY_FORM: ContactFormState = {
  displayName: '',
  email: '',
  title: '',
  department: '',
  countryCode: '',
  phone: '',
  accountId: null,
};

function fromContact(c: Contact): ContactFormState {
  return {
    displayName: c.displayName ?? '',
    email: c.email ?? '',
    title: c.title ?? '',
    department: c.department ?? '',
    countryCode: c.countryCode ?? '',
    phone: c.phone ?? '',
    accountId: c.accountId,
  };
}

function ContactForm({
  wsId,
  initial,
  onCancel,
  onSubmit,
  submitting,
  submitLabel,
}: {
  wsId: string;
  initial: ContactFormState;
  onCancel: () => void;
  onSubmit: (values: ContactFormState) => void;
  submitting: boolean;
  submitLabel: string;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<ContactFormState>(initial);
  const [accountSearch, setAccountSearch] = useState('');

  const accounts = useQuery({
    queryKey: ['accounts', wsId, accountSearch, 'picker'],
    queryFn: () =>
      accountsApi.list({
        workspaceId: wsId,
        search: accountSearch || undefined,
        limit: 10,
      }),
    enabled: !!wsId,
  });

  const set = <K extends keyof ContactFormState>(k: K, v: ContactFormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const canSubmit = form.displayName.trim().length > 0 && !submitting;

  const selectedAccount = accounts.data?.items.find((a) => a.id === form.accountId);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) onSubmit(form);
      }}
      className="space-y-4"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs text-ink-600">
            {t('people.field_display_name')} <span className="text-warn-600">*</span>
          </span>
          <input
            type="text"
            value={form.displayName}
            onChange={(e) => set('displayName', e.target.value)}
            required
            className="mt-1 w-full rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
          />
        </label>
        <label className="block">
          <span className="text-xs text-ink-600">{t('people.field_email')}</span>
          <input
            type="email"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            className="mt-1 w-full rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
          />
        </label>
        <label className="block">
          <span className="text-xs text-ink-600">{t('people.field_title')}</span>
          <input
            type="text"
            value={form.title}
            onChange={(e) => set('title', e.target.value)}
            className="mt-1 w-full rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
          />
        </label>
        <label className="block">
          <span className="text-xs text-ink-600">{t('people.field_department')}</span>
          <input
            type="text"
            value={form.department}
            onChange={(e) => set('department', e.target.value)}
            className="mt-1 w-full rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
          />
        </label>
        <label className="block">
          <span className="text-xs text-ink-600">{t('people.field_country')}</span>
          <div className="mt-1">
            <CountrySelect
              value={form.countryCode}
              onChange={(v) => set('countryCode', v)}
              allowEmpty={{ value: '', label: t('common.select_placeholder') }}
            />
          </div>
        </label>
        <label className="block">
          <span className="text-xs text-ink-600">{t('people.field_phone')}</span>
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => set('phone', e.target.value)}
            className="mt-1 w-full rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
          />
        </label>
      </div>

      <div>
        <span className="text-xs text-ink-600">{t('people.field_account')}</span>
        <input
          type="search"
          placeholder={t('people.account_search_placeholder')}
          value={accountSearch}
          onChange={(e) => setAccountSearch(e.target.value)}
          className="mt-1 w-full rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
        />
        <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-ink-200 divide-y divide-ink-100">
          <button
            type="button"
            onClick={() => set('accountId', null)}
            className={
              'w-full text-left px-3 py-1.5 text-xs ' +
              (form.accountId === null
                ? 'bg-accent-50 text-accent-700'
                : 'text-ink-600 hover:bg-ink-50')
            }
          >
            {t('common.none')}
          </button>
          {(accounts.data?.items ?? []).map((a) => (
            <button
              type="button"
              key={a.id}
              onClick={() => set('accountId', a.id)}
              className={
                'w-full text-left px-3 py-1.5 text-xs ' +
                (form.accountId === a.id
                  ? 'bg-accent-50 text-accent-700'
                  : 'text-ink-700 hover:bg-ink-50')
              }
            >
              <div className="font-medium">{a.legalName}</div>
              {a.domain && <div className="text-ink-500 num">{a.domain}</div>}
            </button>
          ))}
        </div>
        {selectedAccount && (
          <div className="mt-1 text-xs text-ink-500">
            {selectedAccount.legalName}
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="text-sm px-3 py-1.5 rounded-md text-ink-700 hover:bg-ink-100"
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className="text-sm px-3 py-1.5 rounded-md bg-accent-500 text-white font-medium hover:bg-accent-600 disabled:opacity-60"
        >
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

function ContactDrawerBody({ wsId, contact }: { wsId: string; contact: Contact }) {
  const { t, i18n } = useTranslation();

  const account = useQuery({
    queryKey: ['account', contact.accountId],
    queryFn: () => accountsApi.get(contact.accountId!),
    enabled: !!contact.accountId,
  });

  const signals = useQuery({
    queryKey: ['signals', 'contact', contact.id],
    queryFn: () =>
      signalsApi.list({
        workspaceId: wsId,
        contactId: contact.id,
        limit: 5,
      }),
    enabled: !!wsId,
  });

  return (
    <div className="space-y-6 text-sm">
      <section>
        <h3 className="text-xs uppercase tracking-wide text-ink-500 mb-2">
          {t('people.drawer_profile')}
        </h3>
        <dl className="space-y-1.5">
          <div className="flex justify-between gap-3">
            <dt className="text-ink-500">{t('people.field_email')}</dt>
            <dd className="text-ink-900 num">{contact.email || '—'}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-ink-500">{t('people.field_title')}</dt>
            <dd className="text-ink-900">{contact.title || '—'}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-ink-500">{t('people.field_department')}</dt>
            <dd className="text-ink-900">{contact.department || '—'}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-ink-500">{t('people.field_country')}</dt>
            <dd className="text-ink-900">{contact.countryCode || '—'}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-ink-500">{t('people.field_phone')}</dt>
            <dd className="text-ink-900 num">{contact.phone || '—'}</dd>
          </div>
        </dl>
      </section>

      <section>
        <h3 className="text-xs uppercase tracking-wide text-ink-500 mb-2">
          {t('people.drawer_account')}
        </h3>
        <div className="rounded-md border border-ink-200 p-3">
          {contact.accountId ? (
            account.data ? (
              <>
                <div className="font-medium text-ink-900">{account.data.legalName}</div>
                <div className="text-xs text-ink-500 num mt-0.5">
                  {account.data.domain || '—'}
                </div>
              </>
            ) : (
              <div className="text-xs text-ink-500">{t('common.loading')}</div>
            )
          ) : (
            <div className="text-xs text-ink-500">{t('common.none')}</div>
          )}
        </div>
      </section>

      <section>
        <h3 className="text-xs uppercase tracking-wide text-ink-500 mb-2">
          {t('people.drawer_recent_signals')}
        </h3>
        {signals.isLoading && (
          <p className="text-xs text-ink-500">{t('common.loading')}</p>
        )}
        {!signals.isLoading && (signals.data?.items ?? []).length === 0 && (
          <p className="text-xs text-ink-500">{t('people.drawer_no_signals')}</p>
        )}
        <ul className="space-y-2">
          {(signals.data?.items ?? []).map((s) => (
            <li
              key={s.id}
              className="rounded-md border border-ink-200 p-3 text-xs"
            >
              <div className="font-medium text-ink-900">{s.kind}</div>
              <div className="text-ink-500 num mt-0.5">
                {formatLocaleDate(s.occurredAt, i18n.language)} · {s.source}
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* Comments + @mentions on the contact record. */}
      <section className="pt-2 border-t border-ink-200">
        <CommentThread workspaceId={wsId} targetKind="contact" targetId={contact.id} />
      </section>
    </div>
  );
}

export function PeoplePage() {
  const { wsId = '' } = useParams<{ wsId: string }>();
  const [search, setSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);
  const [drawerContact, setDrawerContact] = useState<Contact | null>(null);
  const [fuzzy, setFuzzy] = useState(false);

  const fuzzyActive = fuzzy && search.trim().length > 0;

  const list = useQuery({
    queryKey: ['contacts', wsId, search, fuzzyActive],
    queryFn: async (): Promise<{ items: Contact[] }> => {
      if (fuzzyActive) {
        const r = await contactsApi.searchSemantic({ workspaceId: wsId, q: search.trim(), limit: 50 });
        return { items: r.items };
      }
      const r = await contactsApi.list({ workspaceId: wsId, search: search || undefined, limit: 100 });
      return { items: r.items };
    },
    enabled: !!wsId,
  });

  const reindex = useMutation({
    mutationFn: () => contactsApi.reindex(wsId),
    onSuccess: (r) => {
      if (r.enabled) toast.success(t('common.reindexed', { count: r.indexed }));
      else toast.error(t('common.semantic_unavailable'));
    },
    onError: () => toast.error(t('common.semantic_unavailable')),
  });

  const items: Contact[] = list.data?.items ?? [];
  const total = items.length;

  const filtered = useMemo(
    () =>
      items.filter(
        (c) => !countryFilter || (c.countryCode ?? '').toUpperCase() === countryFilter,
      ),
    [items, countryFilter],
  );


  const create = useMutation({
    mutationFn: (values: ContactFormState) =>
      contactsApi.create({
        workspaceId: wsId,
        displayName: values.displayName,
        email: values.email || undefined,
        title: values.title || undefined,
        department: values.department || undefined,
        countryCode: values.countryCode || undefined,
        phone: values.phone || undefined,
        accountId: values.accountId || undefined,
      } as Partial<Contact> & { workspaceId: string; displayName: string }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts', wsId] });
      setCreateOpen(false);
      toast.success(t('people.created'));
    },
    onError: () => toast.error(t('people.create_failed')),
  });

  const update = useMutation({
    mutationFn: ({ id, values }: { id: string; values: ContactFormState }) =>
      contactsApi.update(id, {
        displayName: values.displayName,
        email: values.email || null,
        title: values.title || null,
        department: values.department || null,
        countryCode: values.countryCode || null,
        phone: values.phone || null,
        accountId: values.accountId,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts', wsId] });
      setEditing(null);
      toast.success(t('people.updated'));
    },
    onError: () => toast.error(t('common.save_failed')),
  });

  const remove = useMutation({
    mutationFn: (id: string) => contactsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts', wsId] });
      toast.success(t('people.deleted'));
    },
    onError: () => toast.error(t('common.delete_failed')),
  });

  const askDelete = async (c: Contact) => {
    const ok = await confirm({
      title: t('people.confirm_delete_title'),
      description: t('people.confirm_delete_body', { name: c.displayName }),
      confirmLabel: t('common.delete'),
      variant: 'danger',
    });
    if (ok) remove.mutate(c.id);
  };

  return (
    <AppShell titleKey="people.title">
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <input
          type="search"
          placeholder={t('common.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-64 rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
        />
        <label
          className="flex items-center gap-1.5 text-xs text-ink-600 cursor-pointer"
          title={t('common.fuzzy_hint')}
        >
          <input type="checkbox" checked={fuzzy} onChange={(e) => setFuzzy(e.target.checked)} />
          {t('common.fuzzy')}
        </label>
        <span className="text-xs text-ink-500">
          {t('people.count_label', { count: total })}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => reindex.mutate()}
            disabled={reindex.isPending}
            className="text-xs px-3 py-1.5 rounded-md border border-ink-300 text-ink-700 hover:bg-ink-50 disabled:opacity-50"
          >
            {t('common.reindex')}
          </button>
          <Link
            to={`/app/workspace/${wsId}/bulk-import`}
            className="text-xs px-3 py-1.5 rounded-md border border-ink-300 text-ink-700 hover:bg-ink-100"
          >
            {t('people.bulk_import_button')}
          </Link>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="text-xs px-3 py-1.5 rounded-md bg-accent-500 text-white hover:bg-accent-600"
          >
            {t('people.new_button')}
          </button>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-2 flex-wrap text-xs">
        <span className="text-ink-600">{t('people.filter_country')}</span>
        <CountrySelect
          value={countryFilter}
          onChange={setCountryFilter}
          allowEmpty={{ value: '', label: t('people.filter_country_all') }}
          width="auto"
          className="min-w-[12rem]"
        />
      </div>

      {list.isLoading && <p className="text-sm text-ink-500">{t('common.loading')}</p>}
      {list.isError && <ErrorState onRetry={() => list.refetch()} />}
      {!list.isLoading && !list.isError && filtered.length === 0 && (
        <div className="rounded-2xl border border-dashed border-ink-300 p-10 text-center text-ink-500">
          <p className="text-sm">{t('people.empty_title')}</p>
          <p className="text-xs mt-2">{t('people.empty_sub')}</p>
        </div>
      )}

      {filtered.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm bg-white rounded-2xl border border-ink-200">
            <thead className="text-left text-ink-500 border-b border-ink-200">
              <tr>
                <th className="py-2.5 px-3 font-medium">{t('people.col_name')}</th>
                <th className="py-2.5 px-3 font-medium">{t('people.col_title')}</th>
                <th className="py-2.5 px-3 font-medium">{t('people.col_department')}</th>
                <th className="py-2.5 px-3 font-medium">{t('people.col_email')}</th>
                <th className="py-2.5 px-3 font-medium">{t('people.col_country')}</th>
                <th className="py-2.5 px-3 font-medium">{t('people.col_last_touch')}</th>
                <th className="py-2.5 px-3 font-medium w-10" aria-label={t('common.actions')} />
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr
                  key={c.id}
                  className="border-b border-ink-100 hover:bg-ink-50 cursor-pointer"
                  onClick={() => setDrawerContact(c)}
                >
                  <td className="py-2.5 px-3">
                    <div className="font-medium text-ink-900">{c.displayName}</div>
                    {c.familyNameKana && (
                      <div className="text-xs text-ink-500">
                        {c.familyNameKana} {c.givenNameKana}
                      </div>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-ink-700">{c.title || '—'}</td>
                  <td className="py-2.5 px-3 text-ink-700">{c.department || '—'}</td>
                  <td className="py-2.5 px-3 text-ink-700 num">{c.email || '—'}</td>
                  <td className="py-2.5 px-3 text-ink-700">{c.countryCode || '—'}</td>
                  <td className="py-2.5 px-3 text-ink-500 text-xs">
                    {c.lastTouchedAt
                      ? formatLocaleDate(c.lastTouchedAt, i18n.language)
                      : '—'}
                  </td>
                  <td
                    className="py-2.5 px-3 text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DropdownMenu.Root>
                      <DropdownMenu.Trigger asChild>
                        <button
                          type="button"
                          aria-label={t('people.row_actions')}
                          className="rounded-md p-1 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Portal>
                        <DropdownMenu.Content
                          align="end"
                          sideOffset={4}
                          className="min-w-[140px] rounded-md border border-ink-200 bg-white p-1 text-sm"
                        >
                          <DropdownMenu.Item
                            onSelect={() => setEditing(c)}
                            className="cursor-pointer rounded-sm px-2 py-1 text-ink-700 outline-none data-[highlighted]:bg-ink-100"
                          >
                            {t('common.edit')}
                          </DropdownMenu.Item>
                          <DropdownMenu.Item
                            onSelect={() => askDelete(c)}
                            className="cursor-pointer rounded-sm px-2 py-1 text-warn-600 outline-none data-[highlighted]:bg-warn-50"
                          >
                            {t('common.delete')}
                          </DropdownMenu.Item>
                        </DropdownMenu.Content>
                      </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={createOpen}
        onOpenChange={setCreateOpen}
        title={t('people.new_title')}
        size="lg"
      >
        <ContactForm
          wsId={wsId}
          initial={EMPTY_FORM}
          onCancel={() => setCreateOpen(false)}
          onSubmit={(values) => create.mutate(values)}
          submitting={create.isPending}
          submitLabel={
            create.isPending ? t('common.creating') : t('common.create')
          }
        />
      </Modal>

      <Modal
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        title={t('people.edit_title')}
        size="lg"
      >
        {editing && (
          <ContactForm
            wsId={wsId}
            initial={fromContact(editing)}
            onCancel={() => setEditing(null)}
            onSubmit={(values) =>
              update.mutate({ id: editing.id, values })
            }
            submitting={update.isPending}
            submitLabel={
              update.isPending ? t('common.updating') : t('common.save')
            }
          />
        )}
      </Modal>

      <Drawer
        open={!!drawerContact}
        onOpenChange={(o) => !o && setDrawerContact(null)}
        title={drawerContact?.displayName ?? ''}
        description={drawerContact?.email ?? undefined}
      >
        {drawerContact && (
          <ContactDrawerBody wsId={wsId} contact={drawerContact} />
        )}
      </Drawer>
    </AppShell>
  );
}

